import { DEFAULT_WEIGHT_UNIT } from '@repo/form'
import type { drops } from '@repo/form/drops'
import { mapCompanyLocationToPlaceValue } from '@repo/features/location'
import type { WeightUnit } from '@repo/schema/load'
import type { CompanyLocation } from '@repo/schema/location'

const buildFieldPath = (path: Array<PropertyKey>): string => {
  if (!path.length) {
    return ''
  }

  return path.reduce<string>((acc, segment, index) => {
    if (typeof segment === 'number') {
      return `${acc}[${segment}]`
    }

    return index === 0 ? String(segment) : `${acc}.${String(segment)}`
  }, '')
}

export const getValidationErrors = (
  result:
    | {
        error: { issues: Array<{ message: string; path: Array<PropertyKey> }> }
        success: false
      }
    | { success: true },
):
  | undefined
  | {
      fields: Record<string, Array<{ message: string }>>
      form?: Array<{ message: string }>
    } => {
  if (result.success) {
    return undefined
  }

  const fields: Record<string, Array<{ message: string }>> = {}
  const formErrors: Array<{ message: string }> = []

  result.error.issues.forEach(issue => {
    const path = buildFieldPath(issue.path)

    if (!path) {
      formErrors.push({ message: issue.message })

      return
    }

    const existing = fields[path]

    if (existing) {
      existing.push({ message: issue.message })
    } else {
      fields[path] = [{ message: issue.message }]
    }

    // CompanyLocationInput only reads errors attached to the `place` field
    // itself, so surface nested place validation errors there as well.
    if (
      issue.path.length >= 4 &&
      issue.path[0] === 'drops' &&
      typeof issue.path[1] === 'number' &&
      issue.path[2] === 'place'
    ) {
      const placePath = buildFieldPath(issue.path.slice(0, 3))
      const placeMessages = fields[placePath] ?? []

      if (!placeMessages.some(message => message.message === issue.message)) {
        placeMessages.push({ message: issue.message })
        fields[placePath] = placeMessages
      }
    }
  })

  if (formErrors.length) {
    return { fields, form: formErrors }
  }

  return { fields }
}

export const getFirstValidationMessage = (errors: ReturnType<typeof getValidationErrors>) => {
  if (!errors) return null
  return errors.form?.[0]?.message ?? Object.values(errors.fields)[0]?.[0]?.message ?? null
}

type DropTimeSource = { from?: null | string; to?: null | string; type?: string }

export const normalizeDropTime = (
  time: DropTimeSource | null | undefined,
): {
  from?: null | string
  to?: null | string
  type: '24' | 'asap' | 'at' | 'between' | 'by' | 'tbc'
} => {
  const type = time?.type ?? 'asap'
  if (
    type !== 'at' &&
    type !== 'by' &&
    type !== 'between' &&
    type !== '24' &&
    type !== 'tbc' &&
    type !== 'asap'
  ) {
    return { type: 'asap' }
  }
  return { from: time?.from, to: time?.to, type }
}

export const hasValidPlace = (drop: { place?: null | Record<string, unknown> }): boolean => {
  const place = drop.place
  if (!place || Object.keys(place).length === 0) return false
  return !!(
    place.place_name ||
    place.place_id ||
    place.lat ||
    place.lng ||
    place.city ||
    place.postcode ||
    place.location_id
  )
}

export const parseDate = (value: unknown): Date | undefined => {
  if (value instanceof Date) return value
  if (typeof value === 'string') {
    const d = new Date(value)
    return isNaN(d.getTime()) ? undefined : d
  }
  return undefined
}

const DEFAULT_RATE_CURRENCY = 'GBP'

export type NewLoadFormValues = {
  company_id: null | number
  contact_id: null | number
  customer_rate: { amount: null | string; currency: string; description: null | string }
  drops: drops
  end_customer_company_id: null | number
  end_customer_contact_id: null | number
  goods_description: null | string
  load_type_id: null | number
  notes: null | string
  organisation_id: null | number
  packaging_type_id: number | undefined
  pallets: null | number | undefined
  requirements: { adr: boolean; dvs: boolean; waste_license: boolean }
  requires_end_customer: boolean
  user_id: null | number
  weight: null | number
  weight_unit: null | WeightUnit
}

type PlaceholderDrop = {
  order: number
  time: { from?: string; to?: string; type: 'asap' | 'between' }
  type: 'collection' | 'delivery'
}

/** The collection and delivery stops a load starts with, before any place is chosen. */
const placeholderDrops: PlaceholderDrop[] = [
  {
    order: 0,
    time: {
      from: '8:00',
      to: '16:00',
      type: 'between',
    },
    type: 'collection',
  },
  {
    order: 0,
    time: {
      type: 'asap',
    },
    type: 'delivery',
  },
]

export const newLoadDefaultValues: NewLoadFormValues = {
  company_id: null,
  contact_id: null,
  customer_rate: {
    amount: null,
    currency: DEFAULT_RATE_CURRENCY,
    description: null,
  },
  drops: placeholderDrops as drops,
  end_customer_company_id: null,
  end_customer_contact_id: null,
  goods_description: '',
  load_type_id: null,
  notes: null,
  organisation_id: null,
  packaging_type_id: undefined,
  pallets: null,
  requirements: {
    adr: false,
    dvs: false,
    waste_license: false,
  },
  requires_end_customer: false,
  user_id: null,
  weight: null,
  weight_unit: null,
}

// The shared place value types the country code as nullable; a saved location always has one.
const toFormDropPlace = (location: CompanyLocation): drops[number]['place'] => {
  const { country_code: countryCode, ...place } = mapCompanyLocationToPlaceValue(location)

  return countryCode == null ? place : { ...place, country_code: countryCode }
}

const mapCustomerRate = (
  financials?: Array<{
    amount: number
    currency: { alias: string }
    description?: null | string
    type: { alias: string }
  }> | null,
): NewLoadFormValues['customer_rate'] => {
  const rate = financials?.find(financial => financial.type.alias === 'rate')

  if (!rate) {
    return {
      amount: null,
      currency: DEFAULT_RATE_CURRENCY,
      description: null,
    }
  }

  return {
    amount: (rate.amount / 100).toString(),
    currency: rate.currency.alias.toUpperCase(),
    description: rate.description ?? null,
  }
}

export const mapDraftToFormValues = (draft: {
  company?: null | { id: number }
  company_id?: null | number
  contact?: null | { id: number }
  contact_id?: null | number
  drops?: Array<{
    [key: string]: unknown
    date?: Date | null | string
    location?: CompanyLocation | null
    location_id?: null | number
    order?: number
    place?: null | (Record<string, unknown> & { location_id?: null | number })
    time?: DropTimeSource | null
    type: 'collection' | 'delivery'
  }>
  end_customer?: null | { company?: null | { id: number }; contact?: null | { id: number } }
  end_customer_company_id?: null | number
  end_customer_contact_id?: null | number
  financials?: Array<{
    amount: number
    currency: { alias: string }
    description?: null | string
    type: { alias: string }
  }> | null
  goods_description?: null | string
  load_type?: null | { id: number }
  notes?: null | string
  organisation_id?: null | number
  packaging_type?: null | { id: number }
  pallets?: null | number
  requirements?: null | { adr?: boolean; dvs?: boolean; waste_license?: boolean }
  user?: null | { id: number }
  weight?: null | number
  weight_unit?: null | WeightUnit
}): NewLoadFormValues => {
  let sourceDrops = draft.drops ?? []

  // Ensure at least collection + delivery placeholders for the form
  if (!Array.isArray(sourceDrops) || sourceDrops.length === 0) {
    sourceDrops = placeholderDrops
  } else if (sourceDrops.length === 1) {
    const existing = sourceDrops[0]
    const missingType = existing.type === 'collection' ? 'delivery' : 'collection'
    const placeholder = placeholderDrops.find(d => d.type === missingType)!
    sourceDrops = existing.type === 'collection' ? [existing, placeholder] : [placeholder, existing]
  }

  return {
    company_id: draft.company?.id ?? draft.company_id ?? null,
    contact_id:
      draft.end_customer?.company?.id || draft.end_customer_company_id
        ? null
        : (draft.contact?.id ?? draft.contact_id ?? null),
    customer_rate: mapCustomerRate(draft.financials),
    drops: sourceDrops.map((drop, i) => {
      const place = drop.place
      const locationId = place?.location_id ?? drop.location_id ?? drop.location?.id
      const nonNullishPlaceEntries = Object.entries(place ?? {}).filter(
        ([, value]) => value != null,
      )
      const locationDerivedPlace = drop.location ? toFormDropPlace(drop.location) : null

      return {
        ...drop,
        date: parseDate(drop.date),
        order: drop.order ?? i,
        place: {
          ...locationDerivedPlace,
          ...Object.fromEntries(nonNullishPlaceEntries),
          ...(locationId == null ? {} : { location_id: locationId }),
        },
        time: normalizeDropTime(drop.time),
      }
    }),
    end_customer_company_id:
      draft.end_customer?.company?.id ?? draft.end_customer_company_id ?? null,
    end_customer_contact_id:
      draft.end_customer?.contact?.id ?? draft.end_customer_contact_id ?? null,
    goods_description: draft.goods_description ?? '',
    load_type_id: draft.load_type?.id ?? null,
    notes: draft.notes ?? null,
    organisation_id: draft.organisation_id ?? null,
    packaging_type_id: draft.packaging_type?.id ?? undefined,
    pallets: draft.pallets ?? null,
    requirements: {
      adr: draft.requirements?.adr ?? false,
      dvs: draft.requirements?.dvs ?? false,
      waste_license: draft.requirements?.waste_license ?? false,
    },
    requires_end_customer: false,
    user_id: draft.user?.id ?? null,
    weight: draft.weight ?? null,
    // A draft carrying a weight from before the unit was recorded would publish
    // without one if it is never edited, so it takes the unit the input shows.
    weight_unit: draft.weight_unit ?? (draft.weight ? DEFAULT_WEIGHT_UNIT : null),
  }
}

export const buildEndCustomerCompanyQueryInput = ({
  filterOrganisationId,
  orgId,
  search,
}: {
  filterOrganisationId?: number
  orgId?: number
  search: string
}) => ({
  'filter[organisation_id]': filterOrganisationId,
  'filter[type]': 'shipper,broker',
  org_id: orgId,
  perPage: 20,
  search,
})

export const resetSavedLocationValues = <T extends { location_id?: unknown; place?: unknown }>(
  drops: T[],
) =>
  drops.map(drop => {
    const place = drop.place
    const { location_id: _locationId, ...withoutLocationId } = drop
    if (!place && !('location_id' in drop)) return drop
    return { ...withoutLocationId, place: place ? {} : place }
  })

type PartyTransitionForm = {
  getFieldValue: (name: 'drops') => drops
  setFieldValue: (
    name: 'contact_id' | 'drops' | 'end_customer_company_id' | 'end_customer_contact_id',
    value: drops | null,
  ) => void
}

const resetRouteLocations = (form: PartyTransitionForm) => {
  form.setFieldValue('drops', resetSavedLocationValues(form.getFieldValue('drops')))
}

export const applyCommercialCustomerChange = (form: PartyTransitionForm) => {
  form.setFieldValue('contact_id', null)
  form.setFieldValue('end_customer_company_id', null)
  form.setFieldValue('end_customer_contact_id', null)
  resetRouteLocations(form)
}

export const applyEndCustomerChange = (form: PartyTransitionForm) => {
  form.setFieldValue('end_customer_contact_id', null)
  resetRouteLocations(form)
}

export const applyEndCustomerSelection = (form: PartyTransitionForm) => {
  applyEndCustomerChange(form)
  form.setFieldValue('contact_id', null)
}

export const isThreePlBroker = ({
  isBroker,
  isMarketplace,
}: {
  isBroker: boolean
  isMarketplace: boolean
}) => isBroker && isMarketplace

export const shouldShowEndCustomerFields = ({
  endCustomerCompanyId,
  isBroker,
  isMarketplace,
}: {
  endCustomerCompanyId: null | number
  isBroker: boolean
  isMarketplace: boolean
}) => isBroker && (isMarketplace || endCustomerCompanyId !== null)

export const shouldShowCompanyDetailsLink = ({
  brokerRepresentsOrganisationId,
  hasBrokerResolutionError,
  hasSelectedCompany,
  isAllOrganisationsSelected,
  isBrokerResolutionPending,
}: {
  brokerRepresentsOrganisationId?: null | number
  hasBrokerResolutionError: boolean
  hasSelectedCompany: boolean
  isAllOrganisationsSelected: boolean
  isBrokerResolutionPending: boolean
}) =>
  hasSelectedCompany &&
  !isBrokerResolutionPending &&
  !hasBrokerResolutionError &&
  (brokerRepresentsOrganisationId == null || isAllOrganisationsSelected)
